import React from 'react';
import { ShieldCheck, Flame, Activity, ArrowRight, DollarSign, Users, Zap } from 'lucide-react';
import { OsCard, AnimatedNumber, Skeleton } from '../theme';

/* Admin Command Center — revenue pulse, hot leads, traceability. */

const money = (v) => `$${(Number(v) || 0).toLocaleString(undefined, { maximumFractionDigits: 0 })}`;

const relTime = (iso, isEs) => {
  if (!iso) return '';
  const m = Math.max(0, Math.round((Date.now() - new Date(iso).getTime()) / 60000));
  if (m < 60) return isEs ? `hace ${m} min` : `${m}m ago`;
  const h = Math.round(m / 60);
  return h < 24 ? (isEs ? `hace ${h} h` : `${h}h ago`) : (isEs ? `hace ${Math.round(h / 24)} d` : `${Math.round(h / 24)}d ago`);
};

function Kpi({ icon: Icon, label, children, tone = 'var(--os-ink)', delay = 0 }) {
  return (
    <OsCard className="p-4 os3-rise" style={{ animationDelay: `${delay}ms` }}>
      <p className="flex items-center gap-1.5 text-[10px] uppercase tracking-[0.14em] font-bold" style={{ color: 'var(--os-ink-faint)' }}>
        <Icon className="w-3.5 h-3.5" style={{ color: 'var(--os-accent)' }} /> {label}
      </p>
      <p className="mt-1.5 text-[26px] font-black tabular-nums leading-tight tracking-tight" style={{ color: tone }}>{children}</p>
    </OsCard>
  );
}

export default function AdminHome({ admin, isEs, onEnterAdmin }) {
  const [showIncidents, setShowIncidents] = React.useState(false);
  if (!admin) {
    return <div className="mt-6 grid grid-cols-2 lg:grid-cols-4 gap-3">{[0, 1, 2, 3].map((i) => <Skeleton key={i} className="h-24" />)}</div>;
  }
  const prod = admin.production || {};
  const prodColor = prod.status === 'GO' ? '#34D399' : prod.status === 'NO-GO' ? '#F87171' : 'var(--os-ink-faint)';
  const sys = admin.system;
  const KIND = {
    affiliate: { es: 'Afiliado', en: 'Affiliate', bg: 'rgba(139,92,246,0.12)', c: '#8B5CF6' },
    prospect: { es: 'Potencial', en: 'Prospect', bg: 'rgba(56,189,248,0.12)', c: '#0284C7' },
    contact: { es: 'Contacto', en: 'Contact', bg: 'rgba(148,163,184,0.15)', c: '#64748B' },
  };
  return (
    <div data-testid="admin-home">
      {sys && (
        <div className="mt-5 rounded-2xl px-4 py-3 flex flex-wrap items-center gap-x-5 gap-y-2 text-[12px]" data-testid="admin-system-strip"
          style={{ background: 'var(--os-surface)', border: '1px solid var(--os-border)' }}>
          <span className="text-[10px] uppercase tracking-[0.15em] font-bold" style={{ color: 'var(--os-ink-faint)' }}>{isEs ? 'Estado del sistema' : 'System status'}</span>
          {sys.services.map((s) => (
            <span key={s.key} className="inline-flex items-center gap-1.5 font-semibold" data-testid={`sys-${s.key}`}
              style={{ color: s.status === 'connected' ? 'var(--os-ink-soft)' : '#F87171' }}>
              <span className="relative inline-flex w-1.5 h-1.5">
                <span className="absolute inset-0 rounded-full animate-ping opacity-60" style={{ background: s.status === 'connected' ? '#34D399' : '#F87171' }} />
                <span className="relative w-1.5 h-1.5 rounded-full" style={{ background: s.status === 'connected' ? '#10B981' : '#EF4444' }} />
              </span>
              {s.label}
            </span>
          ))}
          <span style={{ color: 'var(--os-ink-faint)' }}><b className="tabular-nums" style={{ color: 'var(--os-ink)' }}>{sys.events_today}</b> {isEs ? 'eventos hoy' : 'events today'}</span>
          <button type="button" onClick={() => sys.incidents && setShowIncidents((v) => !v)} data-testid="sys-incidents"
            className={`ml-auto font-bold ${sys.incidents ? 'cursor-pointer hover:opacity-75 underline decoration-dotted underline-offset-4' : 'cursor-default'} transition-opacity`}
            style={{ color: sys.incidents ? '#F59E0B' : '#10B981' }}>
            {sys.incidents ? `⚠ ${sys.incidents} ${isEs ? 'a revisión' : 'to review'} ${showIncidents ? '▴' : '▾'}` : (isEs ? '✓ Sin incidencias' : '✓ No incidents')}
          </button>
        </div>
      )}
      {sys && showIncidents && (
        <div className="mt-2 rounded-2xl px-4 py-3.5" data-testid="admin-incidents-detail"
          style={{ background: 'var(--os-surface)', border: '1px solid rgba(245,158,11,0.35)' }}>
          <p className="text-[10px] uppercase tracking-[0.15em] font-bold" style={{ color: '#F59E0B' }}>{isEs ? 'Qué necesita revisión' : 'What needs review'}</p>
          <ul className="mt-2 space-y-1.5">
            {(admin.activity || []).filter((n) => ['critical', 'warning'].includes(n.severity)).slice(0, 5).map((n, i) => (
              <li key={i} className="flex items-start gap-2 text-[12.5px]" style={{ color: 'var(--os-ink-soft)' }}>
                <span className="mt-1 w-1.5 h-1.5 rounded-full shrink-0" style={{ background: n.severity === 'critical' ? '#EF4444' : '#F59E0B' }} />
                <span>{n.title || n.message || n.type}</span>
              </li>
            ))}
          </ul>
          <p className="mt-2.5 text-[12px] font-semibold" style={{ color: 'var(--os-ink)' }}>
            💡 {isEs ? 'Haz esto: revisa estas alertas en el Centro de Operaciones y márcalas como resueltas. Un sistema en verde vende más.' : 'Do this: review these alerts in the Operations Center and clear them. A green system sells more.'}
          </p>
          {onEnterAdmin && (
            <button onClick={onEnterAdmin} data-testid="admin-incidents-open"
              className="mt-2 inline-flex items-center gap-1.5 rounded-full px-4 py-1.5 text-[12px] font-bold text-white transition-opacity hover:opacity-90" style={{ background: '#F77F11' }}>
              {isEs ? 'Abrir Admin OS' : 'Open Admin OS'} <ArrowRight className="w-3.5 h-3.5" />
            </button>
          )}
        </div>
      )}
      <div className="mt-4 grid grid-cols-2 lg:grid-cols-4 gap-3">
        <Kpi icon={DollarSign} label={isEs ? 'Revenue del mes' : 'Revenue this month'}>
          <AnimatedNumber value={admin.revenue_month} format={money} />
        </Kpi>
        <Kpi icon={Zap} label={isEs ? 'Revenue hoy' : 'Revenue today'} delay={60}>
          <AnimatedNumber value={admin.revenue_today} format={money} />
        </Kpi>
        <Kpi icon={Users} label={isEs ? 'Leads hoy' : 'Leads today'} delay={120}>
          <AnimatedNumber value={admin.leads_today} />
        </Kpi>
        <Kpi icon={ShieldCheck} label={isEs ? 'Producción' : 'Production'} tone={prodColor} delay={180}>
          {prod.status || '—'}
        </Kpi>
      </div>

      <div className="mt-4 grid grid-cols-1 lg:grid-cols-2 gap-4">
        <OsCard className="p-5" glow data-testid="admin-hot-leads">
          <p className="flex items-center gap-1.5 text-[11px] uppercase tracking-[0.15em] font-bold" style={{ color: 'var(--os-accent)' }}>
            <Flame className="w-3.5 h-3.5" /> {isEs ? 'Leads calientes' : 'Hot leads'}
          </p>
          <div className="mt-3 space-y-2.5">
            {(admin.hot_leads || []).length === 0 && (
              <p className="text-[12.5px]" style={{ color: 'var(--os-ink-faint)' }}>{isEs ? 'Sin leads calientes ahora mismo — el CRM sigue vigilando.' : 'No hot leads right now — the CRM keeps watching.'}</p>
            )}
            {(admin.hot_leads || []).map((l) => {
              const k = KIND[l.kind] || KIND.contact;
              return (
                <div key={l.email} className="flex items-center justify-between gap-2">
                  <div className="min-w-0">
                    <p className="text-[13px] font-semibold truncate" style={{ color: 'var(--os-ink)' }}>
                      {l.name || l.email}
                      <span className="ml-1.5 rounded px-1.5 py-0.5 text-[9px] font-bold uppercase align-middle" style={{ background: k.bg, color: k.c }}>{isEs ? k.es : k.en}</span>
                    </p>
                    <p className="text-[11px] truncate" style={{ color: 'var(--os-ink-faint)' }}>
                      {l.source || 'CRM'}{l.restaurant && l.restaurant !== l.name ? ` · ${l.restaurant}` : ''}{(l.opens || l.clicks || l.replies) ? ` · ${l.opens} ${isEs ? 'aperturas' : 'opens'} · ${l.clicks} clicks${l.replies ? ` · ${l.replies} ${isEs ? 'respuestas' : 'replies'}` : ''}` : ''}
                    </p>
                  </div>
                  <span className="shrink-0 rounded-full px-2.5 py-1 text-[11px] font-black tabular-nums"
                    style={{ background: 'var(--os-accent-soft)', color: 'var(--os-accent)' }}>{l.score}</span>
                </div>
              );
            })}
            {(admin.at_risk || []).slice(0, 2).map((l) => (
              <div key={l.email} className="flex items-center justify-between gap-2 opacity-75">
                <div className="min-w-0">
                  <p className="text-[13px] font-semibold truncate" style={{ color: 'var(--os-ink)' }}>{l.name || l.email}</p>
                  <p className="text-[11px]" style={{ color: '#F87171' }}>⌛ {isEs ? 'en riesgo' : 'at risk'} · {l.days_since_activity ?? '—'} {isEs ? 'días sin actividad' : 'days silent'}</p>
                </div>
                <span className="shrink-0 rounded-full px-2.5 py-1 text-[11px] font-black tabular-nums"
                  style={{ background: 'rgba(248,113,113,0.12)', color: '#F87171' }}>{l.score}</span>
              </div>
            ))}
          </div>
        </OsCard>

        <OsCard className="p-5" data-testid="admin-activity">
          <p className="flex items-center gap-1.5 text-[11px] uppercase tracking-[0.15em] font-bold" style={{ color: 'var(--os-ink-faint)' }}>
            <Activity className="w-3.5 h-3.5" style={{ color: 'var(--os-accent)' }} /> {isEs ? 'Trazabilidad · Actividad reciente' : 'Traceability · Recent activity'}
          </p>
          <div className="mt-3 space-y-2.5">
            {(admin.activity || []).length === 0 && (
              <p className="text-[12.5px]" style={{ color: 'var(--os-ink-faint)' }}>{isEs ? 'Sin actividad reciente.' : 'No recent activity.'}</p>
            )}
            {(admin.activity || []).slice(0, 7).map((a) => (
              <div key={a.nid} className="flex items-start gap-2.5">
                <span className="text-[14px] leading-none mt-0.5" aria-hidden>{a.icon}</span>
                <div className="min-w-0 flex-1">
                  <p className="text-[12.5px] font-semibold truncate" style={{ color: 'var(--os-ink)' }}>{isEs ? a.title_es : a.title_en}</p>
                  {a.detail && <p className="text-[11px] truncate" style={{ color: 'var(--os-ink-faint)' }}>{a.detail}</p>}
                </div>
                <span className="text-[10.5px] shrink-0 tabular-nums" style={{ color: 'var(--os-ink-faint)' }}>{relTime(a.at, isEs)}</span>
              </div>
            ))}
          </div>
        </OsCard>
      </div>

      <div className="mt-6 flex flex-col sm:flex-row gap-3">
        <a href="/demo/os" data-testid="admin-demo-btn"
          className="inline-flex items-center justify-center gap-2 rounded-full px-7 py-3.5 text-[14px] font-bold transition-all hover:opacity-80"
          style={{ border: '1px solid var(--os-border)', color: 'var(--os-ink)', background: 'var(--os-surface)' }}>
          🎬 {isEs ? 'Modo Demo (datos de muestra)' : 'Demo Mode (sample data)'}
        </a>
        <button data-testid="admin-vip-invite-btn"
          onClick={() => {
            const card = document.querySelector('[data-testid="hub-vip-invite"]');
            if (card) {
              card.scrollIntoView({ behavior: 'smooth', block: 'center' });
              setTimeout(() => card.querySelector('[data-testid="hub-vip-label"]')?.focus(), 600);
            }
          }}
          className="inline-flex items-center justify-center gap-2 rounded-full px-7 py-3.5 text-[14px] font-bold transition-all hover:opacity-80"
          style={{ border: '1px solid #F77F11', color: '#F77F11', background: 'var(--os-surface)' }}>
          🎟️ {isEs ? 'Invitar VIP a la demo (sin registro)' : 'Invite VIP to demo (no sign-up)'}
        </button>
      </div>
    </div>
  );
}
